import { ReactNode, Component, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import { QueryBuilder, QueryStructure, QueryItemType, Field, FieldsItemType, Operator, Combinator } from '@Primitive/QueryBuilder';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { IconURIPath, PartialIWidgetThingsProps, WidgetLocalizationKeys } from '../WidgetsThingsStructs';
import { getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsElementAll, SettingsType } from '@Primitive/SettingsListType';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import { RamStorage } from '@Utils/RamStorage';
// =======================================================================================================
export interface QueryStructureBuilderData {
    Fields: Field[];
    DefaultOperators: Operator[];
    DefaultFieldOperators: { [key: string]: Operator[] };
    TimePickerEnable: boolean;
    AutoQueryUpdate: boolean;
};

export interface QueryStructureBuilderState {
    Fields: Field[];
    DefaultFieldOperators: { [key: string]: Operator[] };
    Combinators: Combinator[];
}

export function SelectOptionsInitializationJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance('SelectOptionsInitializationJsonSchemeValidator', {
        title: 'SelectDataType',
        type: 'array',
        items: {
            type: 'object',
            properties: {
                key: { type: 'string' },
                label: { type: 'string' },
            },
            required: ['key', 'label']
        },
        minItems: 1
    });
}

interface ISettingsIn {
    fields: Field[];
    queryStructure: QueryStructure[];
    defaultOperators: { [key: string]: Operator[] };
}

export function QueryWidgetSettingsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance('QueryWidgetSettingsJsonSchemeValidator', {
        type: 'object',
        properties: {
            fields: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        key: { type: 'string' },
                        label: { type: 'string' },
                        type: {
                            type: 'number',
                            enum: [0, 1, 2, 3, 4, 5]
                        },
                        useDefaultOperator: { type: 'boolean' },
                        operators: {
                            type: 'array',
                            items: {
                                type: 'object',
                                properties: {
                                    key: { type: ['string', 'number'] },
                                    label: { type: 'string' },
                                },
                                required: ['key', 'label']
                            },
                        },
                        values: {
                            type: 'array',
                            items: {
                                type: 'object',
                                properties: {
                                    key: { type: ['string', 'number'] },
                                    label: { type: 'string' },
                                },
                                required: ['key', 'label']
                            },
                        }
                    },
                    required: ['key', 'type']
                },
            },
            queryStructure: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        id: { type: 'string' },
                        rules: { type: 'array' },
                        type: { type: 'number', enum: [0, 1] },
                        notEnabled: { type: 'boolean' },
                        combinator: { type: 'string' },
                        field: { type: 'string' },
                        value: { enum: ['object', 'string', 'null', 'number'] },
                        operator: { type: 'string' },
                    }
                },
                required: ['id', 'rules', 'type']
            },
            defaultOperators: {
                type: 'object',
                patternProperties: {
                    '^.*$': {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                key: { type: ['string', 'number'] },
                                label: { type: 'string' },
                            },
                            required: ['key', 'label']
                        }
                    }
                }
            }
        },
        required: []
    });
}

const defaultCombinators: Combinator[] = [
    { key: 'and', label: 'AND' },
    { key: 'or', label: 'OR' },
];
// ------------------------------------------------------------------------------------------------------
class QueryStructureBuilder extends Component<PartialIWidgetThingsProps<QueryStructureBuilderData>, QueryStructureBuilderState> {
    // ------------------------------------------------------------------------------------------------------
    private _stateStorage: RamStorage;
    private _savedStructure: QueryStructure[];
    private _prevTs: number = 0;
    // ------------------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<QueryStructureBuilderData>) {
        super(props);

        // get struct from storage
        this._stateStorage = RamStorage.GetInstance();
        this._savedStructure = this._stateStorage.GetState(this.props.ThingKey);
        if (this._savedStructure == null) {
            this._savedStructure = [{
                id: getRandomSmallKey(),
                type: QueryItemType.Group,
                rules: [],
                combinator: defaultCombinators[0].key
            }];
        }
        this.state = { ...this._getStateFromProps(this.props) };
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: PartialIWidgetThingsProps<QueryStructureBuilderData>) {
        if (!_.isEqual(nextProps.WidgetData, this.props.WidgetData) ||
            !_.isEqual(nextProps.LocalizationMap, this.props.LocalizationMap)) {
            this.setState(this._getStateFromProps(nextProps));
        }
        this._updateData(nextProps);
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        // todo: use port states
        this._updateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        // save struct to storage
        this._stateStorage.SetState(this.props.ThingKey, this._savedStructure);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const { width, height, NodeKey, WidgetData } = this.props;
        const { Fields, DefaultFieldOperators, Combinators } = this.state;
        return (
            <div style={{
                padding: 0,
                width: width,
                height: height,
                overflow: 'auto hidden'
            }}>
                <QueryBuilder
                    width={width}
                    height={height}
                    QueryStructure={this._savedStructure}
                    Fields={Fields}
                    DefaultOperators={DefaultFieldOperators}
                    Combinators={Combinators}
                    onQueryStructureChange={this._onQueryStructureChange}
                    updateOutputPort={this._updateOutputPort}
                    TimePickerEnable={WidgetData.TimePickerEnable}
                    localizationKey={NodeKey}
                    HideToolbar={WidgetData.AutoQueryUpdate}
                />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private _onQueryStructureChange = (struct: QueryStructure[]) => this._savedStructure = struct
    // ---------------------------------------------------------------------------------------------------
    private _updateOutputPort = (text: string, json: QueryStructure[]) => {
        const { SendPortEvent } = this.props;
        if (isFunction(SendPortEvent)) {
            // send port event with sql text
            SendPortEvent(PortKeys.textQuery, text);

            // send port event with query structure
            SendPortEvent(PortKeys.treeQuery, JSON.stringify(json));
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _getStateFromProps(nextProps: PartialIWidgetThingsProps<QueryStructureBuilderData>): QueryStructureBuilderState {
        const
            widgetData = nextProps.WidgetData,
            fields = _.cloneDeep(widgetData.Fields),
            combinators = _.cloneDeep(defaultCombinators),
            defaultOperators = _.cloneDeep(widgetData.DefaultFieldOperators),
            map = nextProps.LocalizationMap;

        //  localize field names
        _.forEach(fields, (field) => {
            const value = map['field_' + field.key];
            if (!IsNullOrEmpty(value))
                field.label = value;
        });

        // localize combinators' labels
        _.forEach(combinators, (combinator) => {
            const value = map['combinator_' + combinator.key];
            if (!IsNullOrEmpty(value))
                combinator.label = value;
        });

        // localize DefaultFieldOperators
        _.forEach(defaultOperators,
            (operators) => {
                _.forEach(operators,
                    (operator) => {
                        let value = map['operator_' + operator.key];
                        if (!IsNullOrEmpty(value))
                            operator.label = value;
                    });
            });

        return {
            Fields: fields,
            Combinators: combinators,
            DefaultFieldOperators: defaultOperators
        } as QueryStructureBuilderState;
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateData(props: PartialIWidgetThingsProps<QueryStructureBuilderData>) {
        if (!isDefined(props.PortsStates))
            return;
        // iterate over select port
        _.forEach(props.WidgetData.Fields, f => {
            if (f.type == FieldsItemType.Select)
                this._updateDataFromPort(props, f.key);
        });
        // check settings port
        this._updateDataFromPort(props, PortKeys.fillSettings);
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateDataFromPort(
        props: PartialIWidgetThingsProps<QueryStructureBuilderData>,
        portKey: string
    ) {
        const port = props.PortsStates[portKey];
        if (!isDefined(port))
            return;

        if (isDefined(port.Timestamp)) {
            if (this._prevTs >= port.Timestamp)
                return;

            this._prevTs = port.Timestamp;
        }

        //check portKey
        if (portKey == PortKeys.fillSettings) {
            let settings: ISettingsIn;
            if (port.LastStateStatus == StateStatus.NoData) {
                settings = _.cloneDeep(props.WidgetDefaultValues[PortKeys.fillSettings]);
                if (!isDefined(settings))
                    return;
            }
            else if (port.LastStateStatus == StateStatus.Valid) {
                settings = _.cloneDeep(port.State as ISettingsIn);
            }
            else
                return;

            let willUpdate = false;
            if (isDefined(settings.fields)) {
                willUpdate = true;
                this.setState({ Fields: settings.fields });
            }
            if (isDefined(settings.queryStructure)) {

                // handle empty arrays
                if (settings.queryStructure.length == 0) {
                    settings.queryStructure = [{
                        id: getRandomSmallKey(),
                        type: QueryItemType.Group,
                        rules: [],
                        combinator: defaultCombinators[0].key
                    }];
                }

                this._savedStructure = settings.queryStructure;
                if (!willUpdate)
                    this.forceUpdate();
            }

            const defaultOperators = settings.defaultOperators;
            if (!_.isEmpty(defaultOperators))
                this.setState(s => {
                    // Override default operators.
                    _.forOwn(defaultOperators, (v, k) => {
                        if (_.has(s.DefaultFieldOperators, k))
                            s.DefaultFieldOperators[k] = v;
                    });
                    return s;
                });
        }
        else if (portKey == PortKeys.treeQuery) {
            let queryStructure: QueryStructure[];
            if (port.LastStateStatus == StateStatus.NoData) {
                queryStructure = _.cloneDeep(props.WidgetDefaultValues[PortKeys.treeQuery]);
                if (!isDefined(queryStructure))
                    return;
            }
            else if (port.LastStateStatus == StateStatus.Valid) {
                queryStructure = _.cloneDeep(port.State as QueryStructure[]);
            }
            else
                return;
        }
        else {
            let values: { key: string, label: string }[];
            // select port was sent
            if (port.LastStateStatus == StateStatus.NoData)
                values = _.cloneDeep(props.WidgetDefaultValues[portKey]);
            else if (port.LastStateStatus == StateStatus.Valid)
                values = port.State as { key: string, label: string }[];
            else
                return;

            if (!isDefined(values))
                return;

            this.setState((s) => {
                for (let i = 0; i < s.Fields.length; i++) {
                    const field = s.Fields[i];
                    if (field.type == FieldsItemType.Select && field.key == portKey) {
                        field.values = values;
                        break;
                    }
                }
                return s;
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
}

// ------------------------------------------------------------------------------------------------
class QueryStructureBuilderWidgetSettings extends PureComponent<IWidgetSettingsProps<QueryStructureBuilderData>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<QueryStructureBuilderData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const { SettingListToolbar, WidgetData } = this.props;
        // Render list with settings.
        return <SettingsList
            AutoSave={true}
            Toolbar={SettingListToolbar}
            Data={WidgetData}
            onSaveData={this.onSaveData}
            SettingsMap={[
                {
                    name: '',
                    elements: [
                        {
                            type: SettingsType.Boolean,
                            label: 'Auto Query Update',
                            tooltip: 'Updates the output query (both ports) automatically on every change of structure',
                            dataPath: 'AutoQueryUpdate'
                        },
                        {
                            type: SettingsType.ItemsMgmtDialog,
                            label: 'Fields',
                            tooltip: 'Sets the available fields',
                            dataPath: 'Fields',
                            itemsInfo: [
                                {
                                    type: SettingsType.String,
                                    label: 'Key',
                                    isUnique: true,
                                    dataPath: 'key'
                                },
                                {
                                    type: SettingsType.String,
                                    label: 'Label',
                                    dataPath: 'label'
                                },
                                {
                                    type: SettingsType.DropDown,
                                    label: 'Type',
                                    dataPath: 'type',
                                    items: [
                                        { label: 'Text', value: FieldsItemType.Text },
                                        { label: 'Number', value: FieldsItemType.Number },
                                        { label: 'Boolean', value: FieldsItemType.Boolean },
                                        { label: 'Color', value: FieldsItemType.Color },
                                        { label: 'Date', value: FieldsItemType.Date },
                                        { label: 'Select', value: FieldsItemType.Select },
                                    ]
                                },
                                {
                                    type: SettingsType.Boolean,
                                    label: 'Default Operators',
                                    dataPath: 'useDefaultOperator'
                                },
                                {
                                    type: SettingsType.ItemsMgmtDialog,
                                    label: 'Operators',
                                    dataPath: 'operators',
                                    itemsInfo: [
                                        {
                                            type: SettingsType.String,
                                            label: 'Key',
                                            isUnique: true,
                                            dataPath: 'key'
                                        },
                                        {
                                            type: SettingsType.String,
                                            label: 'Label',
                                            dataPath: 'label'
                                        }
                                    ],
                                    isEnabled: (data, row) => !row.useDefaultOperator,
                                    createNewItem: () => {
                                        return {
                                            key: 'newOperator ' + getRandomSmallKey(),
                                            label: 'New operator',
                                        }
                                    }
                                }
                            ],
                            createNewItem: () => {
                                return {
                                    label: 'newField' + getRandomSmallKey(),
                                    type: FieldsItemType.Text,
                                    key: 'newField',
                                    useDefaultOperator: true,
                                    operators: WidgetData.DefaultOperators
                                }
                            }
                        },
                        {
                            type: SettingsType.Boolean,
                            label: 'Time Picker',
                            tooltip: 'Allow the selection of dates with times',
                            dataPath: 'TimePickerEnable',
                            isHidden: (data: QueryStructureBuilderData) => {
                                if (_.isEmpty(data.Fields))
                                    return true;
                                for (let i = 0; i < data.Fields.length; i++)
                                    if (data.Fields[i].type == FieldsItemType.Select)
                                        return false;
                                return true;
                            }
                        },
                        {
                            type: SettingsType.Group,
                            name: 'Select',
                            elementsFunc: (data: QueryStructureBuilderData) => {
                                const elements: SettingsElementAll<any>[] = [];
                                _.forEach(data.Fields, (field, index) => {
                                    if (field.type == FieldsItemType.Select) {
                                        elements.push({
                                            type: SettingsType.ItemsMgmtDialog,
                                            label: field.label,
                                            dataPath: `Fields[${index}].values`,
                                            itemsInfo: [
                                                {
                                                    type: SettingsType.String,
                                                    label: 'Value',
                                                    isUnique: true,
                                                    dataPath: 'key'
                                                },
                                                {
                                                    type: SettingsType.String,
                                                    label: 'Label',
                                                    dataPath: 'label'
                                                }
                                            ],
                                            createNewItem: () => {
                                                return {
                                                    key: 'New value ' + getRandomSmallKey(),
                                                    label: 'New option',
                                                }
                                            }
                                        })
                                    }
                                })
                                return elements;
                            }
                        },
                        {
                            type: SettingsType.Group,
                            name: 'Default Operators', elementsFunc: (data: QueryStructureBuilderData) => {
                                return _.map(data.DefaultFieldOperators, (value, key) => {
                                    return {
                                        type: SettingsType.ItemsMgmtDialog,
                                        label: key,
                                        tooltip: `Custom operators for ${key.toLowerCase()} type`,
                                        dataPath: `DefaultFieldOperators.${key}`,
                                        itemsInfo: [
                                            {
                                                type: SettingsType.String,
                                                label: 'Key',
                                                isUnique: true,
                                                dataPath: 'key'
                                            },
                                            {
                                                type: SettingsType.String,
                                                label: 'Label',
                                                dataPath: 'label'
                                            },
                                        ],
                                        createNewItem: () => {
                                            return {
                                                key: 'newOperator ' + getRandomSmallKey(),
                                                label: 'New operator',
                                            }
                                        }
                                    }
                                });
                            }
                        }
                    ]
                }
            ]}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
    private onSaveData = (data) => {
        const { onWidgetDataUpdate } = this.props;
        if (isFunction(onWidgetDataUpdate))
            onWidgetDataUpdate(data);
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
const enum PortKeys {
    textQuery = 'QueryBuilderText',
    treeQuery = 'QueryBuilderTree',
    fillSettings = 'QueryBuilderSettings',
}

// Date Picker Description interface
export class QueryStructureBuilderWidget extends IWidgetThingsClass<QueryStructureBuilderData> {
    // ------------------------------------------------------------------------------------------------
    public Render(): ReactNode {
        return <QueryStructureBuilder />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <QueryStructureBuilderWidgetSettings
            key={'Settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }

    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.fillSettings)
            return QueryWidgetSettingsJsonSchemeValidator().DataValidateString(value);

        return SelectOptionsInitializationJsonSchemeValidator().DataValidateString(value);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, this._getPorts());
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // add thing's ports
        _.forEach(this._getPorts(), port => {
            thing.AddPort(port);
        })

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    private _getPorts(): WebThingPort[] {
        // Ports
        let ports: WebThingPort[] = [
            new WebThingPort({
                Name: 'Query String',
                PortKey: PortKeys.textQuery,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data),
            new WebThingPort({
                Name: 'Query Structure',
                PortKey: PortKeys.treeQuery,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Data),
            new WebThingPort(
                {
                    Name: 'Settings',
                    PortKey: PortKeys.fillSettings,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: ''
                },
                WebThingPortType.Data,
                data => this.ValidateValues(PortKeys.fillSettings, data))
        ];
        _.forEach(this.WidgetData.Fields, f => {
            if (f.type == FieldsItemType.Select) {
                ports.push(new WebThingPort(
                    {
                        Name: f.label,
                        PortKey: f.key,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Data,
                    data => this.ValidateValues(f.key, data)
                ));
            }
        });
        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): QueryStructureBuilderData {
        let defaultOperators: Operator[] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' },
            { key: '!=', label: '!=' },
            { key: '<', label: '<' },
            { key: '>', label: '>' },
            { key: '<=', label: '<=' },
            { key: '>=', label: '>=' }
        ];

        let data: QueryStructureBuilderData = {
            Fields: [],
            DefaultOperators: defaultOperators,
            DefaultFieldOperators: {
                'Text': [],
                'Number': [],
                'Date': [],
                'Boolean': [],
                'Color': [],
                'Select': []
            },
            TimePickerEnable: false,
            AutoQueryUpdate: false
        };

        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return QueryStructureBuilderWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data = QueryStructureBuilderWidget.GetDefaultWidgetDataStatic();

        data.DefaultFieldOperators['Text'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' },
            { key: '!=', label: '!=' }
        ];

        data.DefaultFieldOperators['Number'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' },
            { key: '!=', label: '!=' },
            { key: '<', label: '<' },
            { key: '>', label: '>' },
            { key: '<=', label: '<=' },
            { key: '>=', label: '>=' }
        ];

        data.DefaultFieldOperators['Date'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' },
            { key: 'range', label: 'range' }
        ];

        data.DefaultFieldOperators['Boolean'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' }];

        data.DefaultFieldOperators['Color'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' }];

        data.DefaultFieldOperators['Select'] = [
            { key: 'is null', label: 'is null' },
            { key: 'is not null', label: 'is not null' },
            { key: '=', label: '=' }];

        data.Fields = [
            { key: 'firstName', label: 'First Name', type: FieldsItemType.Text, useDefaultOperator: true, operators: data.DefaultFieldOperators['Text'] },
            { key: 'lastName', label: 'Last Name', type: FieldsItemType.Text, useDefaultOperator: true, operators: data.DefaultFieldOperators['Text'] },
            { key: 'age', label: 'Age', type: FieldsItemType.Number, useDefaultOperator: true, operators: data.DefaultFieldOperators['Number'] },
            { key: 'married', label: 'Married', type: FieldsItemType.Boolean, useDefaultOperator: true, operators: data.DefaultFieldOperators['Boolean'] }
        ];

        return {
            Title: 'Query Builder',
            Key: 'QueryStructureBuilderWidget',
            Type: 'QueryStructureBuilderWidget',
            Data: data,
            MinH: 1,
            MinW: 1,
            IconURI: IconURIPath + '/svg/query_builder.svg'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        // expose field names
        _.forEach(this.WidgetData.Fields, (field) => {
            if (!IsNullOrEmpty(field.label))
                res['field_' + field.key] = field.label;
        });

        // expose combinators' labels
        _.forEach(defaultCombinators, (combinator) => {
            res['combinator_' + combinator.key] = combinator.label;
        });

        // expose DefaultFieldOperators
        _.forEach(this.WidgetData.DefaultFieldOperators,
            (operators) => {
                _.forEach(operators,
                    (operator) => {
                        let key;
                        if (!IsNullOrEmpty(operator.label) &&
                            !_.has(res,
                                (key = 'operator_' + operator.key)
                            ))
                            res[key] = operator.label;
                    });
            });

        return res;
    };
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {}; }
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================